import { WeeklyTotalsAssignedToTable } from './tables/WeeklyTotalsAssignedToTable';
import { WeeklyTotalsDisciplineTable } from './tables/WeeklyTotalsDisciplineTable';
import { WeeklyTotalsPatientTable } from './tables/WeeklyTotalsPatientTable';
import { WeeklyTotalsTeamTable } from './tables/WeeklyTotalsTeamTable';
import type { SieveFilterModel } from '~/util/sieve/filter';
import { DataTableFilterInjectContextProvider } from '~/shared/data-tables/query/DataTableFilterInjectContext';
import { useQueryState } from 'nuqs';
import { useGetSchedulingOptions } from '../filters/useGetSchedulingOptions';
import { AssignedToFilter, PatientFilter, PrimaryInsuranceFilter, WeekOfFilter } from '../filters/Filters';
import { QueryDataTableFilterBar } from '~/shared/data-tables/query/QueryDataTableFilterBar';
import { FilterBar } from '~/shared/components/filter-bar/FilterBar';
import { useQueryDataTableOptions } from '~/shared/data-tables/query/useQueryDataTableOptions';
import { ButtonGroupField } from '~/shared/forms/controls/base-fields/ButtonGroupField';
import { GallagherEmployeesFilter, UtilizationThresholdFilter } from './UtilizationFilters';

const groupByOptions = [
	{ id: 'patient-code', label: 'Patient' },
	{ id: 'discipline', label: 'Discipline' },
	{ id: 'assigned-to', label: 'Assigned To' },
	{ id: 'team', label: 'Team' },
	{ id: 'utilization', label: 'Utilization' }
];

export function WeeklyTotals() {
	const { data: options } = useGetSchedulingOptions();
	const { clearSearchOptions } = useQueryDataTableOptions();
	const [selectedGrouping, setSelectedGrouping] = useQueryState('selectedGrouping', { defaultValue: 'patient-code' });
	const defaultFilters: SieveFilterModel[] = [
		{ name: 'weekNumber', operator: '==', value: options.weeks.find(x => x.currentWeek)!.weekNumber.toString() }
	];
	const toggleGrouping = (newGroup: string) => {
		clearSearchOptions();
		setSelectedGrouping(newGroup);
	};

	return (
		<div className="space-y-6">
			<DataTableFilterInjectContextProvider defaultFilters={defaultFilters}>
				<div className="mb-4 flex flex-row items-center gap-4 align-middle">
					<span className="font-semibold">Group By</span>
					<ButtonGroupField
						options={groupByOptions}
						isSelected={(id: string) => selectedGrouping === id}
						onSelect={(id: string) => toggleGrouping(id)}
						label="Category"
						hideLabel
					/>
				</div>

				<QueryDataTableFilterBar>
					<FilterBar.Item label="Week Of">
						<WeekOfFilter />
					</FilterBar.Item>
					{selectedGrouping === 'patient-code' && (
						<>
							<FilterBar.Item label="Patient">
								<PatientFilter />
							</FilterBar.Item>
							<FilterBar.Item label="Insurance">
								<PrimaryInsuranceFilter />
							</FilterBar.Item>
						</>
					)}
					{(selectedGrouping === 'assigned-to' || selectedGrouping === 'utilization') && (
						<FilterBar.Item label="Assigned To">
							<AssignedToFilter />
						</FilterBar.Item>
					)}
					{selectedGrouping === 'utilization' && (
						<>
							<FilterBar.Item>
								<GallagherEmployeesFilter />
							</FilterBar.Item>
							<FilterBar.Item>
								<UtilizationThresholdFilter type="under" />
							</FilterBar.Item>
							<FilterBar.Item>
								<UtilizationThresholdFilter type="over" />
							</FilterBar.Item>
						</>
					)}
				</QueryDataTableFilterBar>

				{(selectedGrouping === 'assigned-to' || selectedGrouping === 'utilization') && <WeeklyTotalsAssignedToTable />}
				{selectedGrouping === 'discipline' && <WeeklyTotalsDisciplineTable />}
				{selectedGrouping === 'patient-code' && <WeeklyTotalsPatientTable />}
				{selectedGrouping === 'team' && <WeeklyTotalsTeamTable />}
			</DataTableFilterInjectContextProvider>
		</div>
	);
}
